import type { ColumnsType } from 'antd/es/table';
import { Tooltip } from 'antd';
import {
  EditSmallIcon,
  EyeCloseIcon,
  EyeIcon,
  TrashIcon,
} from '@dynamika-shared/ui/icons';
import {
  ActionTypeEnum,
  CollectionTagEnum,
  PMTaskDefinition,
  TaskDefinitionTag,
} from '@dynamika-shared/types/pmTaskDefinitions';
import { TaskDefinitionSortState } from './TaskDefinitionTable.utils';
import { ActionsTypeLabels } from './TaskDefinitionForm/components/TaskActions/actionOptions';
import { CollectionTagTypeLabels } from './TaskDefinitionForm/components/TaskCondinions/conditionOptions';

type MakeTaskDefinitionColumns = {
  edit: (task: PMTaskDefinition) => void;
  delete: (task: PMTaskDefinition) => void;
  sortState?: TaskDefinitionSortState;
};

type CellValue = string | number | null;

const sortOrderFor = (
  sortState: TaskDefinitionSortState,
  columnName: keyof PMTaskDefinition
) => {
  if (!sortState) {
    return null;
  }

  const [field, direction] = sortState.split(',');
  if (field !== columnName) {
    return null;
  }

  return direction === 'asc' ? 'ascend' : 'descend';
};

const textCell = (value?: CellValue) => (
  <div className='max-w-full overflow-hidden truncate whitespace-nowrap font-poppins text-[14px] font-medium leading-[24px] text-black'>
    {value || '-'}
  </div>
);

const tooltipTextCell = (value?: CellValue) => {
  const label = value ?? '-';

  return (
    <Tooltip
      color='white'
      styles={{ body: { width: 'max-content' } }}
      title={
        <span className='text-1424 flex items-center pt-[0.125rem] text-black'>
          {label}
        </span>
      }
    >
      {textCell(label)}
    </Tooltip>
  );
};

const descriptionTooltipTextCell = (value?: CellValue) => {
  if (!value) {
    return textCell(value);
  }

  return tooltipTextCell(value);
};

const orderCell = (value?: CellValue) => (
  <div className='max-w-full overflow-hidden truncate whitespace-nowrap font-poppins text-[14px] font-medium leading-[24px] text-spanishGray'>
    {value || '-'}
  </div>
);

const blankHeader = () => (
  <div className='task-definition-empty-header' aria-hidden='true' />
);

const scopeMap: Record<string, string> = {
  '.Patient': 'Patient Level',
  '.Visit': 'Visit Level',
};

const visibleCell = (visible?: boolean) => (
  <Tooltip
    color='white'
    styles={{ body: { width: 'max-content' } }}
    title={
      <span className='text-1424 flex items-center pt-[0.125rem] text-black'>
        {visible ? 'Visible' : 'Invisible'}
      </span>
    }
  >
    <div className='task-definition-visible-icon'>
      {visible ? (
        <EyeIcon className='h-3.5 w-3.5 text-yellowGreen' />
      ) : (
        <EyeCloseIcon className='h-3.5 w-3.5 text-pastelBlue' />
      )}
    </div>
  </Tooltip>
);

const scopeCell = (scope?: PMTaskDefinition['scope']) => (
  <div className='task-definition-badge'>
    {scope?.type ? (scopeMap[scope.type] ?? '-').toUpperCase() : '-'}
  </div>
);

const conditionCell = (condition?: PMTaskDefinition['condition']) => {
  const taskDefinitionTag = (
    condition && 'conditions' in condition && condition.conditions?.[0]
      ? condition.conditions[0]
      : condition
  ) as TaskDefinitionTag | undefined;
  const collectionType = taskDefinitionTag?.collectionType as
    | CollectionTagEnum
    | undefined;

  return (
    <div className='task-definition-badge'>
      {collectionType
        ? (CollectionTagTypeLabels[collectionType] ?? '-').toUpperCase()
        : '-'}
    </div>
  );
};

const actionsCell = (actions?: PMTaskDefinition['actions']) => {
  const actionType = actions?.actions?.[0]?.type as ActionTypeEnum | undefined;
  const actionLabel = actionType
    ? (ActionsTypeLabels[actionType] ?? '-').replace(/\s*Task$/i, '')
    : '-';

  return (
    <div className='task-definition-badge'>
      {actionLabel.toUpperCase()}
    </div>
  );
};

const scopeConditionActionsCell = (task: PMTaskDefinition) => (
  <div className='task-definition-scope-condition-actions'>
    {scopeCell(task.scope)}
    {conditionCell(task.condition)}
    {actionsCell(task.actions)}
  </div>
);

const automaticExecutionCell = (automaticExecution?: boolean) => (
  <div
    className={
      automaticExecution
        ? 'task-definition-automatic-chip task-definition-automatic-chip-true'
        : 'task-definition-automatic-chip task-definition-automatic-chip-false'
    }
  >
    <span className='task-definition-automatic-chip-label'>
      {automaticExecution ? 'A' : 'M'}
    </span>
  </div>
);

const visibilityExecutionCell = (task: PMTaskDefinition) => (
  <div className='task-definition-visibility-execution'>
    {visibleCell(task.visibleForImagingSpecialist)}
    {automaticExecutionCell(task.automaticExecution)}
  </div>
);

export function makeTaskDefinitionColumns(
  props: MakeTaskDefinitionColumns,
  isPM: boolean = false
): ColumnsType<PMTaskDefinition> {
  const columns: ColumnsType<PMTaskDefinition> = [
    {
      title: '#',
      dataIndex: 'taskDefinitionOrder',
      key: 'taskDefinitionOrder',
      width: '5%',
      sorter: (left, right) =>
        (left.taskDefinitionOrder ?? 0) - (right.taskDefinitionOrder ?? 0),
      sortOrder: sortOrderFor(props.sortState, 'taskDefinitionOrder'),
      render: value => orderCell(value ? `#${value}` : '-'),
      className: '!w-[5%]',
      onCell: () => ({ className: 'whitespace-nowrap' }),
    },
    {
      title: blankHeader,
      dataIndex: 'visibleForImagingSpecialist',
      key: 'visibilityExecution',
      width: '4%',
      render: (_, task) => visibilityExecutionCell(task),
      onCell: () => ({ className: 'whitespace-nowrap' }),
    },
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      width: '21%',
      sorter: true,
      sortOrder: sortOrderFor(props.sortState, 'code'),
      render: value => tooltipTextCell(value),
      onCell: () => ({ className: 'whitespace-nowrap' }),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: '18%',
      sorter: true,
      sortOrder: sortOrderFor(props.sortState, 'name'),
      render: value => tooltipTextCell(value),
    },
    {
      title: (
        <div className='task-definition-description-header'>Description</div>
      ),
      dataIndex: 'description',
      key: 'description',
      width: '22%',
      render: value => descriptionTooltipTextCell(value),
    },
    {
      title: blankHeader,
      dataIndex: 'scope',
      key: 'scopeConditionActions',
      width: '25%',
      render: (_, task) => scopeConditionActionsCell(task),
      onCell: () => ({ className: 'whitespace-nowrap' }),
    },
  ];

  if (!isPM) {
    columns.push({
      title: <div className='task-definition-action-header'>Action</div>,
      dataIndex: 'action',
      key: 'action',
      width: '7%',
      align: 'right',
      render: (_, task) => (
        <div className='flex justify-end gap-0.5'>
          <Tooltip
            color='white'
            styles={{ body: { width: 'max-content' } }}
            title={
              <span className='text-1424 flex items-center pt-[0.125rem] text-black'>
                Edit
              </span>
            }
          >
            <EditSmallIcon
              className='cursor-pointer hover:text-dodgerBlue'
              width={24}
              height={24}
              onClick={() => props.edit(task)}
              data-testid={`editTaskDefinition-${task.id}`}
            />
          </Tooltip>
          <Tooltip
            color='white'
            styles={{ body: { width: 'max-content' } }}
            title={
              <span className='text-1424 flex items-center pt-[0.125rem] text-black'>
                Delete
              </span>
            }
          >
            <TrashIcon
              className='cursor-pointer hover:text-dodgerBlue'
              width={24}
              height={24}
              onClick={() => props.delete(task)}
              data-testid={`deleteTaskDefinition-${task.id}`}
            />
          </Tooltip>
        </div>
      ),
    });
  }

  return columns;
}
